import { supabase } from "@/integrations/supabase/client";
import type { Folder, Conversation, Document, Message, AskResponse } from "../types";

const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    Authorization: `Bearer ${session?.access_token}`,
  };
};

// ========== FOLDERS ==========
export const fetchFolders = async (): Promise<Folder[]> => {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-folders`,
    { headers }
  );
  const data = await response.json();
  return data.folders || [];
};

export const createFolder = async (name: string): Promise<Folder> => {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-folders`,
    {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }
  );
  const data = await response.json();
  return data.folder;
};

export const updateFolder = async (id: string, name: string): Promise<Folder> => {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-folders/${id}`,
    {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }
  );
  const data = await response.json();
  return data.folder;
};

export const deleteFolder = async (id: string): Promise<void> => {
  const headers = await getAuthHeader();
  await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-folders/${id}`,
    {
      method: 'DELETE',
      headers,
    }
  );
};

// ========== CONVERSATIONS ==========
export const fetchConversations = async (folderId?: string): Promise<Conversation[]> => {
  const headers = await getAuthHeader();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-conversations`);
  if (folderId) url.searchParams.set('folderId', folderId);
  
  const response = await fetch(url.toString(), { headers });
  const data = await response.json();
  return data.conversations || [];
};

export const createConversation = async (folderId: string, title?: string): Promise<Conversation> => {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-conversations`,
    {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderId, title }),
    }
  );
  const data = await response.json();
  return data.conversation;
};

export const deleteConversation = async (id: string): Promise<void> => {
  const headers = await getAuthHeader();
  await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-conversations/${id}`,
    {
      method: 'DELETE',
      headers,
    }
  );
};

// ========== DOCUMENTS ==========
export const fetchDocuments = async (conversationId: string): Promise<Document[]> => {
  const headers = await getAuthHeader();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-documents`);
  url.searchParams.set('conversationId', conversationId);
  
  const response = await fetch(url.toString(), { headers });
  const data = await response.json();
  return data.documents || [];
};

export const uploadDocument = async (conversationId: string, file: File): Promise<Document> => {
  const headers = await getAuthHeader();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('conversationId', conversationId);
  
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-upload`,
    {
      method: 'POST',
      headers,
      body: formData,
    }
  );
  const data = await response.json();
  return data.document;
};

export const deleteDocument = async (id: string): Promise<void> => {
  const headers = await getAuthHeader();
  await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-documents/${id}`,
    {
      method: 'DELETE',
      headers,
    }
  );
};

// ========== MESSAGES ==========
export const fetchMessages = async (conversationId: string): Promise<Message[]> => {
  const headers = await getAuthHeader();
  const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-messages`);
  url.searchParams.set('conversationId', conversationId);
  
  const response = await fetch(url.toString(), { headers });
  const data = await response.json();
  return data.messages || [];
};

export const askQuestion = async (conversationId: string, question: string): Promise<AskResponse> => {
  const headers = await getAuthHeader();
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatplus-ask`,
    {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversationId, question }),
    }
  );
  const data = await response.json();
  return data;
};
